import { useMemo } from 'react';

import { useSankeyRenderContext } from '../../contexts/sankeyRenderContext';
import { measureText } from '../../diagram/labelPaddings';
import { ep } from '../../diagram/numbers';
import { getNodeTransform } from '../../diagram/transform';
import { SankeyNode } from '../../diagram/types';
import { LabelPosEnum } from '../../generated-api';
import { useSankeyNodeInteraction } from '../SankeyInteractionProvider/utils';
import SankeyLabelBackground from './SankeyLabelBackground';

type Props = {
  node: SankeyNode;
};

const SankeyLabel: React.FC<Props> = ({ node }) => {
  const { cfg, labelPaddings, stagesMidpoint } = useSankeyRenderContext();
  const {
    isFocused,
    isHighlighted,
    handleFocus,
    handleBlur,
    handleMouseOut,
    handleMouseOver,
    handleMouseDown,
    handleDoubleClick,
  } = useSankeyNodeInteraction(node);

  const transform = getNodeTransform(node);

  // Which side of the node will the label be on?
  const isLabelOnLeft = useMemo(() => {
    switch (cfg.label_pos) {
      case LabelPosEnum.BEFORE:
        return true;
      case LabelPosEnum.AFTER:
        return false;
      case LabelPosEnum.OUTSIDE:
        return node.stage <= stagesMidpoint;
      // Nodes are positioned at the diagram's outer edge, with labels toward the center.
      case LabelPosEnum.INSIDE:
        return node.stage >= stagesMidpoint;
    }
  }, [cfg, node]);

  const showValue = !(
    node.use_custom_amount_label && node.custom_amount_label.trim() === ''
  );
  const showComparisonLine =
    cfg.show_comparison_line &&
    node.labelComparisonLine &&
    !(
      node.use_custom_comparison_label &&
      node.custom_comparison_label.trim() === ''
    );
  const totalLines = 1 + (showValue ? 1 : 0) + (showComparisonLine ? 1 : 0);

  const nameLineWidth = measureText(cfg, node.name).width;
  const valueLineWidth = showValue
    ? measureText(cfg, node.labelValue).width
    : 0;
  const comparisonLineWidth = showComparisonLine
    ? measureText(cfg, node.labelComparisonLine).width
    : 0;
  const maxLineWidth = Math.max(
    nameLineWidth,
    valueLineWidth,
    comparisonLineWidth
  );

  const fontSize = cfg.label.font.size;
  const lineHeight = fontSize * cfg.label.lineHeightMultiplier;

  const label = isLabelOnLeft
    ? {
        textAnchor: 'end',
        x: node.origPos.x - labelPaddings.lblMarginLeft,
        y: node.origPos.y + node.dy / 2 - (totalLines * lineHeight) / 2,
      }
    : {
        textAnchor: 'start',
        x: node.origPos.x + node.dx + labelPaddings.lblMarginRight,
        y: node.origPos.y + node.dy / 2 - (totalLines * lineHeight) / 2,
      };

  const backgroundRect = isLabelOnLeft
    ? {
        x: label.x - maxLineWidth - labelPaddings.outer,
        y: label.y - labelPaddings.top,
        w: labelPaddings.inner + maxLineWidth + labelPaddings.outer,
        h: labelPaddings.top + totalLines * lineHeight + labelPaddings.bot,
      }
    : {
        x: label.x - labelPaddings.inner,
        y: label.y - labelPaddings.top,
        w: labelPaddings.inner + maxLineWidth + labelPaddings.outer,
        h: labelPaddings.top + totalLines * lineHeight + labelPaddings.bot,
      };

  return (
    <g
      cursor="pointer"
      fill={cfg.label.color}
      fontSize={`${cfg.label.font.size}px`}
      onClick={() => (!isFocused ? handleFocus() : handleBlur())}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
      onMouseDown={handleMouseDown}
      onDoubleClick={handleDoubleClick}
      transform={transform}
    >
      <SankeyLabelBackground
        cfg={cfg}
        isHighlighted={isHighlighted}
        boundingRect={backgroundRect}
      />
      <g transform={`translate(${ep(label.x)},${ep(label.y)})`}>
        <text
          fontWeight={cfg.font.weight.bold}
          textAnchor={label.textAnchor}
          dy={ep(fontSize)}
        >
          {node.name}
        </text>
        {showValue && (
          <text
            textAnchor={label.textAnchor}
            y={ep(lineHeight)}
            dy={ep(fontSize)}
          >
            {node.labelValue}
          </text>
        )}
        {showComparisonLine && (
          <text
            textAnchor={label.textAnchor}
            y={ep((showValue ? 2 : 1) * lineHeight)}
            dy={ep(fontSize)}
          >
            {node.labelComparisonLine}
          </text>
        )}
      </g>
    </g>
  );
};

export default SankeyLabel;
